import { getTemplateDataTableStyles } from './DataTable.styles'
import type {
  CallbackEvent,
  Pagination,
  TableColumn,
  TableRow,
  TableRowCellButtonClick,
  TableRowCellButtonLink,
  TableRowCellDropdown,
  TableRowCellSlot,
} from './types/Table.type'

import { getBootstrapIconUrl, BootstrapIcon } from '../Global/icons'
import {
  tryLoadDependencies,
  tryLoadLightDOMStyles,
  WebAwesomeComponent,
} from '../Global/loadDependency'
import { dispatchAnalyticEvent } from '../Global/analyticsEvents'

const svgSortUp = getBootstrapIconUrl(BootstrapIcon.SortUp)
const svgSortDown = getBootstrapIconUrl(BootstrapIcon.SortDown)

const dataTableTemplate = document.createElement('template')
dataTableTemplate.innerHTML = `
<div part="table-container" class="table-responsive ts-stack">
  <table part="table">
    <thead part="thead">
      <tr part="thead-row"></tr>
    </thead>
    <tbody part="tbody">
    </tbody>
  </table>
  <div part="pagination" class="pagination-wrapper"></div>
</div>
`

const sortableHeaderTemplate = document.createElement('template')
sortableHeaderTemplate.innerHTML = `
<wa-button exportparts="base:column-sort-button" variant="neutral" appearance="plain" size="small"><wa-icon exportparts="svg:column-sort-icon" src="${svgSortDown}" label="Sort"></wa-icon></wa-button>
`

const cellWithSubtextTemplate = document.createElement('template')
cellWithSubtextTemplate.innerHTML = `
  <div class="cell-with-subtext" exportparts="cell-with-subtext">
    <div class="cell-text" exportparts="cell-text">{{ text }}</div>
    <div class="cell-subtext" exportparts="cell-subtext">{{ subtext }}</div>
  </div>
`

class DataTable extends HTMLElement {
  private readonly componentName = 'ts-data-table'
  private sortColumn: string | null = null
  private sortDirection: 'asc' | 'desc' = 'asc'
  private sortColumnDataType: 'string' | 'number' | 'boolean' | 'date' | null = null
  private pageInfo: Pagination | null = null
  private dataColumns: TableColumn[] = []
  private dataRows: TableRow[] = []
  private _callback: ((e: CallbackEvent) => void) | null = null
  private loadedDependencies: Set<WebAwesomeComponent> = new Set()

  constructor() {
    super()

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateDataTableStyles().content.cloneNode(true))
    shadow.appendChild(dataTableTemplate.content.cloneNode(true))

    tryLoadLightDOMStyles('DataTable.css')
  }

  set callback(callback: (e: CallbackEvent) => void) {
    this._callback = callback
  }

  connectedCallback() {
    dispatchAnalyticEvent('ascend_table_loaded', 'load', this.componentName)
  }

  static get observedAttributes() {
    return ['columns', 'rows', 'pagination', 'background', 'spacing']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'columns') {
      this.dataColumns = this.tryGetColumns()
      this.reload()
    } else if (name === 'rows') {
      this.dataRows = this.tryGetRows()
      this.reload()
    } else if (name === 'pagination') {
      this.pageInfo = this.tryGetPagination()
      this.reload()
    } else if (name === 'background') {
      if (newValue === 'striped') {
        this.shadowRoot?.querySelector('table')?.classList.add('table-striped')
      } else {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-striped')
      }
    } else if (name === 'spacing') {
      if (newValue === 'large') {
        this.shadowRoot?.querySelector('table')?.classList.add('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-condensed')
      } else if (newValue === 'condensed') {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.add('table-spacing-condensed')
      } else {
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-large')
        this.shadowRoot?.querySelector('table')?.classList.remove('table-spacing-condensed')
      }
      this.reload()
    }
  }

  private tryGetColumns(): TableColumn[] {
    const columns = this.getAttribute('columns')
    if (!columns) {
      return []
    }
    try {
      return JSON.parse(columns)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return []
  }

  private tryGetRows(): TableRow[] {
    const rows = this.getAttribute('rows')
    if (!rows) {
      return []
    }
    try {
      return JSON.parse(rows)
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return []
  }

  private tryGetPagination(): Pagination | null {
    const dataPagination = this.getAttribute('pagination')
    if (!dataPagination) {
      return null
    }
    try {
      const parsedPagination = JSON.parse(dataPagination)
      parsedPagination.analyticsContextKey =
        parsedPagination?.analyticsContextKey ?? 'ts-data-table'
      return parsedPagination
    } catch (e) {
      console.error('Invalid JSON:', e)
    }
    return null
  }

  private reload() {
    this.updatePaginationUi()

    if (this.sortColumn) {
      this.dataRows.sort((a, b) => {
        const aValue = a.data?.find((cell) => cell.colKey === this.sortColumn)?.value || ''
        const bValue = b.data?.find((cell) => cell.colKey === this.sortColumn)?.value || ''

        // handle date sorting by looking at column data type
        if (this.sortColumnDataType === 'date') {
          const aDate = new Date(aValue as string)
          const bDate = new Date(bValue as string)
          if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) {
            return 0
          }
          if (aDate < bDate) return this.sortDirection === 'asc' ? -1 : 1
          if (aDate > bDate) return this.sortDirection === 'asc' ? 1 : -1
          return 0
        }

        if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    // set column headers
    const tblColumnsRoot = this.shadowRoot?.querySelector('thead tr')
    if (tblColumnsRoot) {
      tblColumnsRoot.innerHTML = '' // Clear existing headers

      for (const column of this.dataColumns) {
        const th = document.createElement('th')
        th.textContent = column.text || ''
        th.part = 'header'
        if (column.sortable) {
          this.internalTryAddDependencies(document, [
            WebAwesomeComponent.Icon,
            WebAwesomeComponent.Button,
          ])

          const btnTemplate = sortableHeaderTemplate.content.cloneNode(true) as HTMLElement
          const waButton = btnTemplate.querySelector('wa-button')
          if (waButton) {
            if (column.key == this.sortColumn) {
              btnTemplate
                .querySelector('wa-icon')
                ?.setAttribute('src', this.sortDirection === 'asc' ? svgSortUp : svgSortDown)
            }

            waButton.addEventListener('click', () => {
              if (this.pageInfo) {
                this.pageInfo.currentPage = 1
              }

              // Toggle sort direction if same column, otherwise default to 'asc'
              if (this.sortColumn === column.key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                this.sortColumnDataType = column.dataType || 'string'
              } else {
                this.sortColumn = column.key || null
                this.sortDirection = 'asc'
                this.sortColumnDataType = column.dataType || 'string'
              }

              // Fire analytics event for table sorting
              dispatchAnalyticEvent('ascend_table_sorted', 'click', this.componentName, {
                column: `${column.text || 'unknown'}`,
                direction: `${this.sortDirection}`,
              })

              this.reload()
            })
          }
          th.appendChild(btnTemplate)
        }
        tblColumnsRoot.appendChild(th)
      }
    }

    // set rows and cells
    const tblBody = this.shadowRoot?.querySelector('tbody')
    if (tblBody) {
      tblBody.innerHTML = '' // Clear existing rows

      let dataToDisplay = this.dataRows
      if (this.pageInfo != null) {
        const start = (this.pageInfo.currentPage - 1) * this.pageInfo.pageSize
        const end = start + this.pageInfo.pageSize
        dataToDisplay = dataToDisplay.slice(start, end)
      }

      for (const row of dataToDisplay) {
        const tr = document.createElement('tr')
        tr.part = 'row'
        for (const cell of row.data || []) {
          const td = document.createElement('td')
          td.part = 'cell'

          if (cell.type === 'slot') {
            const slotElement = document.createElement('slot')
            const slotCell = cell as TableRowCellSlot
            const sanitizedSlotName = slotCell.slotName
              ? slotCell.slotName.replace(/[^a-zA-Z0-9\-_]/g, '')
              : ''
            slotElement.name = sanitizedSlotName
            td.appendChild(slotElement)
          } else if (cell.type === 'button-link') {
            this.internalTryAddDependencies(document, [WebAwesomeComponent.Button])

            const buttonLink: TableRowCellButtonLink = cell as TableRowCellButtonLink
            const button = document.createElement('wa-button')
            button.part = 'cell-button'
            button.setAttribute('exportparts', 'base:cell-button-base')
            button.setAttribute('appearance', 'outlined')

            if (this.getAttribute('spacing') === 'condensed') {
              button.setAttribute('size', 'small')
              td.classList.add('cell-button-condensed')
            }
            button.innerText = cell.text || ''
            button.setAttribute('href', buttonLink.link?.href || '#')
            if (buttonLink.link?.target) {
              button.setAttribute('target', buttonLink.link?.target || '_self')
            }

            button.addEventListener('click', () => {
              // Fire analytics event for cell click
              dispatchAnalyticEvent(
                'ascend_table_cellbuttonlink_clicked',
                'click',
                this.componentName,
                {
                  label: `${button.innerText || 'unknown'}`,
                },
              )
            })

            td.appendChild(button)
          } else if (cell.type === 'button-click') {
            this.internalTryAddDependencies(document, [WebAwesomeComponent.Button])

            const btnCell: TableRowCellButtonClick = cell as TableRowCellButtonClick
            const button = document.createElement('wa-button')
            button.part = 'cell-button'
            button.setAttribute('exportparts', 'base:cell-button-base')
            button.setAttribute('appearance', 'outlined')
            button.setAttribute('variant', 'neutral')
            button.setAttribute('size', 'small')
            button.innerText = cell.text || ''

            button.addEventListener('click', () => {
              // Fire analytics event for table sorting
              dispatchAnalyticEvent(
                'ascend_table_cellbutton_clicked',
                'click',
                this.componentName,
                {
                  label: `${button.innerText || 'unknown'}`,
                },
              )

              if (this._callback) {
                this._callback({ actionType: 'click-cell', key: btnCell.clickEvent?.key ?? '' })
              }
            })

            td.appendChild(button)
          } else if (cell.type === 'dropdown') {
            this.internalTryAddDependencies(document, [
              WebAwesomeComponent.Button,
              WebAwesomeComponent.Dropdown,
              WebAwesomeComponent.DropdownItem,
            ])

            const dropdownCell = cell as TableRowCellDropdown
            const dd = document.createElement('wa-dropdown')
            const button = document.createElement('wa-button')
            button.slot = 'trigger'
            button.part = 'cell-dropdown-button'
            button.setAttribute('exportparts', 'base:cell-button-base')
            button.setAttribute('appearance', 'outlined')
            button.setAttribute('variant', 'neutral')
            button.setAttribute('size', 'small')
            button.setAttribute('with-caret', '')
            button.innerText = cell.text || ''
            dd.appendChild(button)

            if (dropdownCell.items) {
              for (const item of dropdownCell.items) {
                const option = document.createElement('wa-dropdown-item')
                option.textContent = item.text

                option.addEventListener('click', () => {
                  // Fire analytics event for table sorting
                  dispatchAnalyticEvent(
                    'ascend_table_celldropdownitem_selected',
                    'click',
                    this.componentName,
                    {
                      label: `${option.textContent || 'unknown'}`,
                    },
                  )

                  if (this._callback) {
                    this._callback({ actionType: 'click-dropdownitem', key: item.key ?? '' })
                  }
                })
                dd.appendChild(option)
              }
            }
            td.appendChild(dd)
          } else if (cell.subtext && cell.subtext.length > 0) {
            const cellTemplate = cellWithSubtextTemplate.content.cloneNode(true) as HTMLElement
            const textElement = cellTemplate.querySelector('.cell-text')
            if (textElement) {
              textElement.textContent = cell.text || ''
            }

            const subtextElement = cellTemplate.querySelector('.cell-subtext')
            if (subtextElement) {
              subtextElement.textContent = cell.subtext || ''
            }

            td.appendChild(cellTemplate)
          } else {
            td.textContent = cell.text || ''
          }
          tr.appendChild(td)
        }
        tblBody.appendChild(tr)
      }
    }
  }

  private updatePaginationUi() {
    const paginationWrapper = this.shadowRoot?.querySelector('.pagination-wrapper')
    if (!this.shadowRoot || !paginationWrapper) {
      return
    }

    if (this.pageInfo == null || (this.pageInfo.autoHide && this.pageInfo.pageSize <= 0)) {
      paginationWrapper.classList.add('hide')
      return
    }

    paginationWrapper.classList.remove('hide')

    const pc =
      this.shadowRoot.getElementById('pagination') ??
      this.createPaginationControl(paginationWrapper)

    pc.setAttribute('data', JSON.stringify(this.pageInfo))
  }

  private createPaginationControl(container: Element): HTMLElement {
    const pc = document.createElement('ts-pagination')
    pc.setAttribute('id', 'pagination')
    container.appendChild(pc)
    ;(pc as any).callback = (e: any) => {
      if (e.actionType === 'page-selected') {
        if (this.pageInfo) {
          this.pageInfo.currentPage = e.page || 1
          this.reload()
        }
      }
    }

    return pc
  }

  private internalTryAddDependencies(document: Document, components: WebAwesomeComponent[]) {
    components.forEach((component) => {
      if (!this.loadedDependencies.has(component)) {
        tryLoadDependencies(document, [component])
        this.loadedDependencies.add(component)
      }
    })
  }
}

if (!customElements.get('ts-data-table')) {
  customElements.define('ts-data-table', DataTable)
}
export { DataTable }
